import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, FileText, CalendarDays, ArrowLeftRight, Mail, Phone, ChevronRight, BookOpen, Info, Users, GraduationCap, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  variant?: 'programs' | 'admissions' | 'about' | 'experience';
}

export default function Sidebar({ variant = 'programs' }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  if (variant === 'admissions') {
    return (
      <aside className="w-full lg:w-1/4 space-y-12 animate-in fade-in slide-in-from-right-8 duration-700">
        {/* Navigation Sidebar */}
        <div className="space-y-4">
          <h4 className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-6 px-2">In this section</h4>
          <nav className="flex flex-col">
            {[
              { label: t('nav_admissions'), href: '/admissions' },
              { label: t('info_future_students'), href: '/admissions/future' },
              { label: t('info_international_students'), href: '/admissions/international' },
              { label: t('scholarships_hero_badge'), href: '/admissions/scholarships' }
            ].map((link, idx) => (
              <Link 
                key={idx} 
                to={link.href} 
                className={`block font-black border-b border-gray-100 py-4 px-2 transition-all text-sm uppercase tracking-wider ${
                  isActive(link.href) ? 'text-gold bg-gray-50 border-gold' : 'text-navy hover:bg-gray-50 hover:text-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Key Dates Card */}
        <div className="bg-navy text-white p-8 shadow-xl relative overflow-hidden rounded-sm group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gold rotate-45 translate-x-16 -translate-y-16 opacity-20 group-hover:scale-110 transition-transform"></div>
          <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
            <CalendarDays className="w-6 h-6 text-gold" /> {t('admissions_calendar_title', 'Dates Clés')}
          </h3>
          <div className="space-y-6">
            <div>
              <div className="text-gold font-black uppercase text-xs tracking-[0.2em] mb-1">{t('admissions_calendar_open', 'Ouverture')}</div>
              <div className="text-xl font-semibold">{t('admissions_calendar_open_date', 'Janvier 2026')}</div>
            </div>
            <div className="h-px bg-white/10"></div>
            <div>
              <div className="text-gold font-black uppercase text-xs tracking-[0.2em] mb-1">{t('admissions_calendar_close', 'Date Limite')}</div>
              <div className="text-xl font-semibold text-red-400">{t('admissions_calendar_close_date', '30 Juin 2026')}</div>
            </div>
          </div>
        </div>

        {/* Help CTA */}
        <div className="bg-gold p-8 text-white rounded-sm shadow-lg hover:brightness-105 transition-all">
          <h3 className="text-2xl font-black mb-4 uppercase tracking-tight">{t('sidebar_help_title', "Besoin d'aide ?")}</h3>
          <p className="text-sm mb-8 opacity-95 leading-relaxed font-medium">
            {t('sidebar_help_desc', 'Notre bureau des admissions est disponible pour vous accompagner.')}
          </p>
          <div className="space-y-4 font-black text-sm">
            <div className="flex items-center gap-3 bg-white/10 p-3 rounded-sm">
              <Mail className="w-5 h-5" /> admissions@estad.edu
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-full lg:w-1/4 space-y-12 animate-in fade-in slide-in-from-left-8 duration-700">
      {/* Navigation Sidebar */}
      <div className="space-y-4">
        <h4 className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-6 px-2">In this section</h4>
        <nav className="flex flex-col">
          {[
            { label: t('nav_programs'), href: '/programs' },
            { label: t('bachelor_title'), href: '/programs/bachelor' },
            { label: t('master_title'), href: '/programs/master' },
            { label: t('nav_research'), href: '/research' },
            { label: t('nav_partnerships'), href: '/partnerships' }
          ].map((link, idx) => (
            <Link 
              key={idx} 
              to={link.href} 
              className={`block font-black border-b border-gray-100 py-4 px-2 transition-all text-sm uppercase tracking-wider ${
                isActive(link.href) ? 'text-gold bg-gray-50 border-gold' : 'text-navy hover:bg-gray-50 hover:text-gold'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Why ESTAD Card */}
      <div className="bg-gray-50 p-8 border-l-[6px] border-gold shadow-sm rounded-sm">
        <h3 className="text-xl font-black mb-6 uppercase tracking-tighter text-navy">{t('why_title', 'Why Choose ESTAD?')}</h3>
        <ul className="space-y-6">
          {[
            t('why_card_innovation_desc_short', 'Official OMPI CATI Center registration for IP support.'),
            t('research_raft_title_short', 'RAFT Approach: Research As a Form of Teaching.'),
            t('programs_7step_title_short', '7-Step Student-Entrepreneur coaching journey.')
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-4 group">
              <CheckCircle2 className="w-6 h-6 text-gold flex-shrink-0 group-hover:scale-110 transition-transform" />
              <span className="text-sm leading-snug font-medium text-gray-700">{item}</span>
            </li>
          ))}
        </ul>
        <Link to="/about/why-estad" className="inline-flex items-center gap-2 mt-8 text-navy font-black underline hover:text-gold transition-colors text-[10px] uppercase tracking-widest group">
          {t('link_vision_more', 'Learn more')} <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      {/* Resources Sidebar Nav */}
      <div className="space-y-4">
        <h4 className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-6 px-2">{t('sidebar_resources_label', 'Resources')}</h4>
        <nav className="flex flex-col">
          {[
            { label: "Course Catalog (PDF)", href: "/library" },
            { label: "Academic Calendar", href: "/closures" },
            { label: "Student Portal", href: "/student-experience" }
          ].map((link, idx) => (
            <Link 
              key={idx} 
              to={link.href} 
              className="block text-navy font-black border-b border-gray-100 py-4 px-2 hover:bg-gray-50 hover:text-gold transition-all text-sm uppercase tracking-wider"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </aside>
  );
}
